import { type FlightDocument } from '../../Datasource/FlightDocuments/FlightDocument';
import { isFlightDocumentSaved } from '../../Datasource/FlightDocuments/FlightDocument';
import { compareNewestFirstThenById, deleteByUploadState } from './captureRecordShared';

export type DocumentEdit = {
    id: string;
    title?: string | null;
    deleted?: boolean;
};

export const selectStripDocuments = (documents: FlightDocument[]): FlightDocument[] =>
    documents.filter((document) => !document.deleted).sort(compareNewestFirstThenById);

export const applyDocumentCaption = (
    documents: FlightDocument[],
    documentId: string,
    caption: string
): FlightDocument[] => {
    const trimmed = caption.trim();

    return documents.map((document) =>
        document.id === documentId ? { ...document, title: trimmed.length ? trimmed : null } : document
    );
};

export const applyDocumentDeletion = (
    documents: FlightDocument[],
    documentId: string
): { documents: FlightDocument[]; idsToPurge: string[] } => {
    const { idsToPurge, records } = deleteByUploadState(documents, documentId, isFlightDocumentSaved);

    return { documents: records, idsToPurge };
};

export const collectDocumentEdits = (
    storedDocuments: FlightDocument[],
    submittedDocuments: FlightDocument[]
): DocumentEdit[] => {
    const storedById = new Map(storedDocuments.map((document) => [document.id, document]));
    const edits: DocumentEdit[] = [];
    submittedDocuments.forEach((submitted) => {
        const stored = storedById.get(submitted.id);
        if (!stored) {
            return;
        }
        const edit: DocumentEdit = { id: submitted.id };
        if ((submitted.title ?? null) !== (stored.title ?? null)) {
            edit.title = submitted.title ?? null;
        }
        // never back from deleted: no screen undeletes a document, so a live copy of a deleted one is only stale
        if (submitted.deleted && !stored.deleted) {
            edit.deleted = true;
        }
        if (edit.title !== undefined || edit.deleted !== undefined) {
            edits.push(edit);
        }
    });

    return edits;
};

export const mergeDocumentEdits = (storedDocuments: FlightDocument[], edits: DocumentEdit[]): FlightDocument[] => {
    const editedById = new Map(edits.map((edit) => [edit.id, edit]));

    return storedDocuments
        .filter((stored) => editedById.has(stored.id))
        .map((stored) => {
            const edit = editedById.get(stored.id)!;

            return {
                ...stored,
                ...(edit.title === undefined ? {} : { title: edit.title }),
                ...(edit.deleted === undefined ? {} : { deleted: edit.deleted }),
            };
        });
};

// Structurally typed so it can describe the adapter's display items as well as raw documents.
export const describeDocumentAttachment = (
    document: Pick<FlightDocument, 'deferralId' | 'legId' | 'squawkId'>
): string | null => {
    if (document.squawkId) {
        return 'a squawk (defect report)';
    }
    if (document.deferralId) {
        return 'a deferral';
    }
    if (document.legId) {
        return 'this leg';
    }

    return null;
};
